import React, { useContext } from "react";
import { Outlet, Route, Routes, Navigate } from "react-router-dom";

import { UserStateContext } from "../src/components/context/user-state-context.js";
import Page404 from "./pages/404";
import Page403 from "./pages/403";
import { DashboardList } from "./pages/dashboard/dashboard-list.component.js";
import { BillingLanding } from "./pages/billing/billing-landing.component.js";
import { EmployeeRequest } from "./pages/employee-portal/employee-request.component.js";
import { QuickBooksCustomerList } from "./pages/billing/quickbooks-customer/quickbooks-customer-list.component.js";
import { QuickBooksInvoiceList } from "./pages/billing/quickbooks-invoice-list/list.component.js";
import { QBMailerInvoiceList } from "./pages/billing/quickbooks-mailer-invoice/list.component.js";
import { VoiceAgentAdminList } from "./pages/billing/voice-agent-admin/voice-agent-admin-list.component.js";
import { RunbookList } from "./pages/release-runbook/runbook-list.component.js";
import { RunbookDetail } from "./pages/release-runbook/runbook-detail.component.js";

// Opt-in route gating. Every authenticated surface below MUST live inside
// a <Route element={<RequireAccess access="..." />}> group — a bare
// <Route> for a page component would inherit AuthCheck's cinch_employee
// gate and be visible to every Cinch employee, which is exactly the
// accidental-exposure risk we want this file to eliminate.
//
// To add a new access tier (e.g. "cinch-support"), add a role key to
// INTERNAL_ROLES on the backend and extend ACCESS_LEVELS with a
// `(user) => user?.internal_roles?.includes("...")` — never a
// hardcoded email list here.
const ACCESS_LEVELS = {
  "cinch-employee": (user) => !!user?.internal_roles?.includes("cinch_employee"),
  "billing-admin": (user) => !!user?.internal_roles?.includes("billing_admin"),
  "cinch-engineer": (user) => !!user?.internal_roles?.includes("cinch_engineer"),
};

const RequireAccess = ({ access }) => {
  const { user } = useContext(UserStateContext);
  if (!(access in ACCESS_LEVELS)) {
    throw new Error(
      `<RequireAccess> requires an 'access' prop matching one of: ${Object.keys(ACCESS_LEVELS).join(", ")}. Got: ${JSON.stringify(access)}`,
    );
  }
  return ACCESS_LEVELS[access](user) ? <Outlet /> : <Page403 />;
};

export const AppRoutes = () => {
  return (
    <Routes>
      <Route element={<RequireAccess access="cinch-employee" />}>
        <Route path="/dashboard" element={<DashboardList />} />
      </Route>

      <Route element={<RequireAccess access="cinch-engineer" />}>
        <Route path="/release-runbook" element={<RunbookList />} />
        <Route path="/release-runbook/:code" element={<RunbookDetail />} />
      </Route>

      <Route element={<RequireAccess access="billing-admin" />}>
        <Route path="/request" element={<EmployeeRequest />} />
        <Route path="/billing" element={<BillingLanding />} />
        <Route path="/billing/quickbooks-customer-list" element={<QuickBooksCustomerList />} />
        <Route path="/billing/quickbooks-invoice-list" element={<QuickBooksInvoiceList />} />
        <Route path="/billing/quickbooks-mailer-invoice-list" element={<QBMailerInvoiceList />} />
        <Route path="/billing/voice-agents" element={<VoiceAgentAdminList />} />
      </Route>

      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Page404 />} />
    </Routes>
  );
};
